// „Anrufen ▾“: Power Dialer starten. Auf der Startseite (Inbox) und in der Leiste als Knopf mit den eigenen Smart
// Views (die aus der Seitenleiste); auf der Lead-Seite stecken dieselben Punkte im Anrufen-Menü (useDialerMenuePunkte).
// Läuft schon eine Sitzung, steht oben „Weiter anrufen: <Smart View>“.
import { useMemo } from "react";
import type { SmartViewKurz } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Icon } from "../components/Icon";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { angeheftete, istTrenner, useSmartViews } from "../daten/smartviews";
import { useTelefonie } from "./TelefonieProvider";

/** Eigene Smart Views wie in der Seitenleiste: angeheftete, sonst die eigenen (höchstens 15), nur Lead-Ansichten. */
export function useEigeneSmartViews(): SmartViewKurz[] {
  const { views } = useSmartViews();
  const { nutzer } = useAuth();
  return useMemo(() => {
    const liste = (views ?? []).filter((v) => v.type === "lead" && !istTrenner(v.name));
    const pins = angeheftete(liste);
    if (pins.length) return pins;
    const eigene = new Set([nutzer?.id, nutzer?.close_user_id].filter(Boolean) as string[]);
    return liste
      .filter((v) => v.owner_id && eigene.has(v.owner_id))
      .sort((a, b) => a.name.localeCompare(b.name, "de"))
      .slice(0, 15);
  }, [views, nutzer]);
}

/**
 * Menüpunkte „Power Dialer starten“. `bevorzugt` = die Smart View, aus der man kam (steht zuerst); sonst die Auswahl
 * der eigenen Smart Views. Läuft eine Sitzung: ganz oben „Weiter anrufen: <Name>“.
 */
export function useDialerMenuePunkte(bevorzugt?: { id: string; name: string } | null): MenuePunkt[] {
  const t = useTelefonie();
  const eigene = useEigeneSmartViews();
  const aus = t.status === "nicht_eingerichtet" || t.status === "initialisiert";
  const grund = aus ? (t.grund ?? "Telefonie ist nicht bereit.") : undefined;
  return useMemo(() => {
    const punkte: MenuePunkt[] = [];
    const d = t.dialer;
    if (d) {
      punkte.push({
        label: `Weiter anrufen: ${d.sitzung.smart_view_name}`,
        icon: "telefon",
        deaktiviert: !!t.aktiverAnruf,
        hinweis: t.aktiverAnruf ? "Es läuft ein Gespräch (rechts oben „Nächster Anruf“)." : undefined,
        onClick: () => void t.dialerWeiter(),
      });
    }
    const start = (id: string) => () => void t.dialerStarten(id, { modus: "weiter" });
    const gesperrt = aus || !!t.aktiverAnruf;
    if (bevorzugt) {
      punkte.push({
        label: `Power Dialer starten: ${bevorzugt.name}`,
        icon: "telefon",
        trenner: punkte.length > 0,
        deaktiviert: gesperrt,
        hinweis: grund ?? (t.aktiverAnruf ? "Es läuft schon ein Anruf." : undefined),
        onClick: start(bevorzugt.id),
      });
    }
    const rest = eigene.filter((v) => v.id !== bevorzugt?.id);
    if (rest.length) {
      punkte.push({ label: bevorzugt ? "Andere Smart View" : "Power Dialer starten mit", ueberschrift: true, trenner: punkte.length > 0 });
      for (const v of rest) {
        punkte.push({
          label: v.name,
          icon: "telefon",
          deaktiviert: gesperrt,
          hinweis: grund ?? (t.aktiverAnruf ? "Es läuft schon ein Anruf." : undefined),
          onClick: start(v.id),
        });
      }
    }
    if (!punkte.length) punkte.push({ label: "Keine eigenen Smart Views (in der Seitenleiste anheften)", deaktiviert: true });
    return punkte;
  }, [t, eigene, bevorzugt, aus, grund]);
}

/** Knopf „Anrufen ▾“ mit den eigenen Smart Views (Startseite, Leiste ohne Anruf). */
export function AnrufenKnopf({ klein = false }: { klein?: boolean }) {
  const punkte = useDialerMenuePunkte();
  return (
    <MenueKnopf
      knopfKlasse={`knopf knopf--primaer tel-anrufenknopf${klein ? " knopf--klein" : ""}`}
      titel="Anrufen: Power Dialer mit einer Smart View starten"
      punkte={punkte}
      ausrichtung="end"
      breite={300}
    >
      <Icon name="telefon" groesse={14} />
      <span>Anrufen</span>
      <Icon name="chevron-unten" groesse={12} />
    </MenueKnopf>
  );
}
